import { v4 as uuidv4 } from 'uuid';
import { persistedState } from '@/utils/store-helpers';
import request from '@/utils/request';
import { CHATBOT_PANEL_DEFAULT_WIDTH, clampChatbotPanelWidth } from '@/utils/chatbotLayout';
import { resolveChatbotTheme } from '@/utils/chatbotThemes';

const chatbotSettings = persistedState('settings', 'chatbotSettings');

export const moduleState = {
  settings: chatbotSettings.initial({
    isOpen: false,
    sessionId: uuidv4(),
    clientId: null,
    hasUserMessages: false,
  }),
  hasUnreadMessages: false,
};

export const getters = {
  settings: state => state.settings,
  isOpen: state => state.settings.isOpen,
  sessionId: state => state.settings.sessionId,
  panelWidth: state => state.settings.panelWidth ?? CHATBOT_PANEL_DEFAULT_WIDTH,
  sessionClientId: state => state.settings.clientId ?? null,
  theme: state => resolveChatbotTheme(state.settings.theme),
  hasUnreadMessages: state => state.hasUnreadMessages,
};

export const actions = {
  // Binds the chat session to a client. Starts a new session when the current
  // session was started on a different client and the user sent messages in it.
  // Returns whether a new session started.
  bindSessionToClient({ state, commit }, clientId) {
    if (clientId == null) {
      return false;
    }

    const previousClientId = state.settings.clientId ?? null;
    const hadUserMessages = state.settings.hasUserMessages === true;
    commit('setSessionClientId', clientId);

    if (previousClientId == null || previousClientId === clientId || !hadUserMessages) {
      return false;
    }

    commit('resetSessionId');
    return true;
  },

  async loadHistory({ state }) {
    const response = await request.get('/api/prompt/history?maxMessages=50&lookbackDays=30', {
      headers: {
        'Session-Id': state.settings.sessionId,
      },
      errorMessage: 'Failed to get chat history.',
    });

    return response.data;
  },
};

export const mutations = {
  setSettings: chatbotSettings.set,
  setIsOpen(state, isOpen) {
    state.settings.isOpen = isOpen;
    chatbotSettings.set(state, state.settings);
  },
  resetSessionId(state) {
    state.settings.sessionId = uuidv4();
    state.settings.hasUserMessages = false;
    chatbotSettings.set(state, state.settings);
  },
  setSessionClientId(state, clientId) {
    state.settings.clientId = clientId;
    chatbotSettings.set(state, state.settings);
  },
  setSessionHasUserMessages(state, hasUserMessages) {
    state.settings.hasUserMessages = hasUserMessages;
    chatbotSettings.set(state, state.settings);
  },
  setPanelWidth(state, width) {
    state.settings.panelWidth = clampChatbotPanelWidth(width);
    chatbotSettings.set(state, state.settings);
  },
  setTheme(state, theme) {
    state.settings.theme = resolveChatbotTheme(theme);
    chatbotSettings.set(state, state.settings);
  },
  setHasUnreadMessages(state, hasUnread) {
    state.hasUnreadMessages = hasUnread;
  },
};
